import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  PageContainer,
  Progress,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Building2,
  ChevronDown,
  ChevronRight,
  Globe,
  Hotel,
  Image,
  Languages,
  MapPin,
  Mic,
  Route,
  FileText,
  ImagePlus,
} from 'lucide-react';
import { useState } from 'react';

interface HealthData {
  summary: {
    totalGreaterAreas: number;
    totalAttractions: number;
    totalTours: number;
    totalHotels: number;
    totalLanguages: number;
    overallCompleteness: number;
  };
  languages: Array<{
    id: string;
    code: string;
    name: string;
    isDefault: boolean;
  }>;
  greaterAreas: Array<{
    id: string;
    name: string;
    city: string | null;
    state: string | null;
    country: string | null;
    status: string | null;
    hasImage: boolean;
    hasDescription: boolean;
    completeness: number;
    attractions: {
      total: number;
      withImage: number;
      withDescription: number;
      withLongDescription: number;
      withVoiceover: number;
      withLongVoiceover: number;
      withGallery: number;
    };
    tours: {
      total: number;
      withImage: number;
      withDescription: number;
      withVoiceover: number;
    };
    hotels: {
      total: number;
      withImage: number;
      withDescription: number;
    };
    neighborhoods: {
      total: number;
    };
    translations: Record<
      string,
      {
        attractions?: {
          translated: number;
          total: number;
          withDescription: number;
          withVoiceover: number;
        };
        tours?: {
          translated: number;
          total: number;
          withDescription: number;
          withVoiceover: number;
        };
      }
    >;
  }>;
}

function getColorClass(pct: number): string {
  if (pct >= 80) return 'text-emerald-600';
  if (pct >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function getProgressColor(pct: number): string {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

function getBadgeVariant(
  pct: number,
): 'success' | 'warning' | 'destructive' {
  if (pct >= 80) return 'success';
  if (pct >= 40) return 'warning';
  return 'destructive';
}

function pct(count: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((count / total) * 100);
}

function StatRow({
  label,
  count,
  total,
  icon: Icon,
}: {
  label: string;
  count: number;
  total: number;
  icon: React.ElementType;
}) {
  const percentage = pct(count, total);
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-[140px] text-muted-foreground">{label}</span>
      <div className="flex-1 relative">
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${getProgressColor(percentage)}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <span className={`min-w-[80px] text-right font-medium ${getColorClass(percentage)}`}>
        {count}/{total} ({percentage}%)
      </span>
    </div>
  );
}

function GreaterAreaCard({
  area,
  languages,
}: {
  area: HealthData['greaterAreas'][number];
  languages: HealthData['languages'];
}) {
  const [expanded, setExpanded] = useState(false);
  const isShell =
    area.attractions.total === 0 &&
    area.tours.total === 0 &&
    area.hotels.total === 0;

  const location = [area.city, area.state, area.country]
    .filter(Boolean)
    .join(', ');

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base">{area.name}</CardTitle>
                {area.status && (
                  <Badge
                    variant={
                      area.status === 'published'
                        ? 'success'
                        : area.status === 'draft'
                          ? 'secondary'
                          : 'warning'
                    }
                    className="text-xs"
                  >
                    {area.status}
                  </Badge>
                )}
                {isShell && (
                  <Badge variant="destructive" className="text-xs">
                    Shell
                  </Badge>
                )}
              </div>
              {location && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {location}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <span
                  className={`text-lg font-semibold ${getColorClass(area.completeness)}`}
                >
                  {area.completeness}%
                </span>
              </div>
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Quick stats row */}
          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {area.attractions.total} attractions
            </span>
            <span className="flex items-center gap-1">
              <Route className="h-3 w-3" />
              {area.tours.total} tours
            </span>
            <span className="flex items-center gap-1">
              <Hotel className="h-3 w-3" />
              {area.hotels.total} hotels
            </span>
          </div>

          {/* Completeness bar */}
          <div className="mt-2">
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(area.completeness)}`}
                style={{ width: `${area.completeness}%` }}
              />
            </div>
          </div>
        </CardHeader>
      </button>

      {expanded && (
        <CardContent className="pt-0 space-y-5 border-t">
          {/* Greater Area own fields */}
          <div className="pt-4">
            <h4 className="text-sm font-medium text-zinc-900 mb-2">
              Greater Area
            </h4>
            <div className="flex gap-4 text-sm">
              <span
                className={`flex items-center gap-1.5 ${area.hasImage ? 'text-emerald-600' : 'text-red-500'}`}
              >
                <Image className="h-4 w-4" />
                Image {area.hasImage ? 'set' : 'missing'}
              </span>
              <span
                className={`flex items-center gap-1.5 ${area.hasDescription ? 'text-emerald-600' : 'text-red-500'}`}
              >
                <FileText className="h-4 w-4" />
                Description {area.hasDescription ? 'set' : 'missing'}
              </span>
            </div>
          </div>

          {/* Attractions */}
          {area.attractions.total > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-2">
                Attractions ({area.attractions.total})
              </h4>
              <div className="space-y-2">
                <StatRow
                  label="Image"
                  count={area.attractions.withImage}
                  total={area.attractions.total}
                  icon={Image}
                />
                <StatRow
                  label="Description"
                  count={area.attractions.withDescription}
                  total={area.attractions.total}
                  icon={FileText}
                />
                <StatRow
                  label="Long description"
                  count={area.attractions.withLongDescription}
                  total={area.attractions.total}
                  icon={FileText}
                />
                <StatRow
                  label="Voiceover"
                  count={area.attractions.withVoiceover}
                  total={area.attractions.total}
                  icon={Mic}
                />
                <StatRow
                  label="Long voiceover"
                  count={area.attractions.withLongVoiceover}
                  total={area.attractions.total}
                  icon={Mic}
                />
                <StatRow
                  label="Gallery"
                  count={area.attractions.withGallery}
                  total={area.attractions.total}
                  icon={ImagePlus}
                />
              </div>
            </div>
          )}

          {/* Tours */}
          {area.tours.total > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-2">
                Tours ({area.tours.total})
              </h4>
              <div className="space-y-2">
                <StatRow
                  label="Image"
                  count={area.tours.withImage}
                  total={area.tours.total}
                  icon={Image}
                />
                <StatRow
                  label="Description"
                  count={area.tours.withDescription}
                  total={area.tours.total}
                  icon={FileText}
                />
                <StatRow
                  label="Voiceover"
                  count={area.tours.withVoiceover}
                  total={area.tours.total}
                  icon={Mic}
                />
              </div>
            </div>
          )}

          {/* Hotels */}
          {area.hotels.total > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-2">
                Hotels ({area.hotels.total})
              </h4>
              <div className="space-y-2">
                <StatRow
                  label="Image"
                  count={area.hotels.withImage}
                  total={area.hotels.total}
                  icon={Image}
                />
                <StatRow
                  label="Description"
                  count={area.hotels.withDescription}
                  total={area.hotels.total}
                  icon={FileText}
                />
              </div>
            </div>
          )}

          {/* Translations */}
          {languages.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-2">
                Translations
              </h4>
              <div className="space-y-3">
                {languages
                  .filter((l) => !l.isDefault)
                  .map((lang) => {
                    const t = area.translations[lang.code];
                    if (!t) return null;
                    const aT = t.attractions;
                    const tT = t.tours;
                    return (
                      <div key={lang.id} className="space-y-1.5">
                        <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-700">
                          <Globe className="h-3.5 w-3.5" />
                          {lang.name}
                        </div>
                        <div className="pl-5 space-y-1.5 text-sm text-muted-foreground">
                          {aT && aT.total > 0 && (
                            <div className="flex items-center justify-between">
                              <span>
                                Attractions: {aT.translated}/{aT.total}{' '}
                                translated
                              </span>
                              <span>
                                {aT.withDescription} desc, {aT.withVoiceover}{' '}
                                voice
                              </span>
                            </div>
                          )}
                          {tT && tT.total > 0 && (
                            <div className="flex items-center justify-between">
                              <span>
                                Tours: {tT.translated}/{tT.total} translated
                              </span>
                              <span>
                                {tT.withDescription} desc, {tT.withVoiceover}{' '}
                                voice
                              </span>
                            </div>
                          )}
                          {(!aT || aT.total === 0) &&
                            (!tT || tT.total === 0) && (
                              <span className="text-muted-foreground/60">
                                No translatable content
                              </span>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-muted/50 p-2.5">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-2xl font-semibold tracking-tight">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-5 w-80 mt-2" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-16" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    </PageContainer>
  );
}

export function DataHealthPage() {
  const apiClient = useApiClient();

  const { data, isLoading, error, refetch } = useQuery<{
    success: boolean;
    data: HealthData;
  }>({
    queryKey: ['data-health'],
    queryFn: () => apiClient.get('/data-health'),
  });

  if (isLoading) return <LoadingSkeleton />;

  if (error || !data?.data) {
    return (
      <PageContainer>
        <ErrorState
          title="Failed to load data health"
          description={
            error?.message || 'An unexpected error occurred'
          }
          action={{
            label: 'Retry',
            onClick: () => refetch(),
          }}
        />
      </PageContainer>
    );
  }

  const { summary, languages, greaterAreas } = data.data;

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
            Data Health
          </h1>
          <p className="text-muted-foreground mt-1">
            Content completeness overview across all greater areas
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard
            label="Greater Areas"
            value={summary.totalGreaterAreas}
            icon={Building2}
          />
          <SummaryCard
            label="Attractions"
            value={summary.totalAttractions}
            icon={MapPin}
          />
          <SummaryCard
            label="Tours"
            value={summary.totalTours}
            icon={Route}
          />
          <SummaryCard
            label="Hotels"
            value={summary.totalHotels}
            icon={Hotel}
          />
          <SummaryCard
            label="Languages"
            value={summary.totalLanguages}
            icon={Languages}
          />
        </div>

        {/* Overall completeness */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium text-zinc-900">
                  Overall Completeness
                </span>
              </div>
              <Badge variant={getBadgeVariant(summary.overallCompleteness)}>
                {summary.overallCompleteness}%
              </Badge>
            </div>
            <Progress
              value={summary.overallCompleteness}
              className="h-3"
            />
          </CardContent>
        </Card>

        {/* Greater Area cards */}
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">
            Greater Areas
          </h2>
          <div className="space-y-3">
            {greaterAreas.map((area) => (
              <GreaterAreaCard
                key={area.id}
                area={area}
                languages={languages}
              />
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
